import cn from 'classnames';
import * as React from 'react';

import Text from '../text';
import useInputValidation from '../use-input-validation';

import { themes, variants, TextInput as Props } from './text-input.types';
import { useEffect } from 'react';
import SvgError from '@sats-group/icons/16/error';
import SvgInfo from '@sats-group/icons/16/info';

const RefTextInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      customErrorMessages,
      defaultValue,
      disabled,
      hasError,
      hasMovingLabel,
      helpText,
      hiddenLabel,
      icon,
      label,
      name,
      onChange = () => {},
      placeholder,
      required,
      theme,
      type = 'text',
      variant = variants.large,
      ...restProps
    },
    ref,
  ) => {
    const [isError, setIsError] = React.useState(hasError);
    const [validationOnChange, onInvalid, error] = useInputValidation(
      customErrorMessages,
      customErrorMessages ? customErrorMessages.defaultError : undefined,
      isError,
    );

    useEffect(() => {
      setIsError(hasError);
    }, [hasError]);

    return (
      <label
        className={cn('text-input', {
          'text-input--theme-dark': theme === themes.dark,
          'text-input--theme-light': theme === themes.light,
          'text-input--disabled': disabled,
          'text-input--error': error || isError,
          'text-input--moving-label': icon ? false : hasMovingLabel,
          'text-input--icon': icon,
          'text-input--variant-small': variant === variants.small,
          'text-input--variant-large': variant === variants.large,
        })}
      >
        <div className="text-input__wrapper">
          <input
            {...restProps}
            className="text-input__input"
            defaultValue={defaultValue}
            disabled={disabled}
            required={required}
            name={name}
            onInvalid={e => onInvalid(e)}
            onChange={e => {
              onChange(e);
              validationOnChange(e);
              setIsError(false); // NOTE: We want to reset error state on change to not confuse users.
            }}
            // NOTE: Using " " as placeholder for moving label theme to enable using `:placeholder-shown` to determine when to move the label
            placeholder={
              icon ? placeholder : hasMovingLabel ? ' ' : placeholder
            }
            ref={ref}
            type={type}
            aria-label={icon || hiddenLabel ? label : undefined}
          />

          {icon ? (
            <div className="text-input__icon">{icon}</div>
          ) : hiddenLabel ? null : (
            <Text
              className="text-input__label"
              theme={Text.themes.emphasis}
              size={
                variant === variants.small ? Text.sizes.small : Text.sizes.basic
              }
            >
              {label}
              {required ? (
                <span className="text-input__asterisk">*</span>
              ) : null}
            </Text>
          )}
        </div>

        {helpText ? (
          <div className="text-input__help">
            <div className="text-input__help-icon">
              <SvgInfo />
            </div>
            <Text size={Text.sizes.interface}>{helpText}</Text>
          </div>
        ) : null}
        {/* NOTE: This is aria-hidden because reporting of validation errors is handled by the browser */}
        {error ? (
          <div aria-hidden="true" className="text-input__error">
            <div className="text-input__error-icon">
              <SvgError />
            </div>
            <Text size={Text.sizes.interface}>{error}</Text>
          </div>
        ) : null}
      </label>
    );
  },
);

// NOTE: If this isn't set, stack traces say "forwardRef" instead of "TextInput"
RefTextInput.displayName = 'TextInput';

const TextInput: typeof RefTextInput & {
  themes: typeof themes;
  variants: typeof variants;
} = Object.assign(RefTextInput, { themes, variants });

export default TextInput;
